// src/utils/formatters.ts

const TRILLION = 1e12;
const BILLION = 1e9;
const MILLION = 1e6;
const THOUSAND = 1e3;

// TODO: add pre and post syombol like $5/people:
// where $ is pre and /poeple is post
// and update everywhere accordingly.
export const formatLargeNumberCell = (
  value: string | number | null | undefined,
  decimals: number = 2,
  symbol: string = ""
): string => {
  // Remove this code if this looks too empty
  if (value == 0 || value === "0") return "";

  // Handle null, undefined, "NA", NaN cases
  if (value === null || value === undefined) return "N/A";
  if (value === "NA" || value === "N/A") return "N/A";

  // Convert string to number if needed
  const value_number = typeof value === "number" ? value : Number(value);

  // Check if the number is valid
  if (isNaN(value_number)) return "N/A";

  // Format valid numbers based on magnitude
  if (Math.abs(value_number) >= TRILLION)
    return `${symbol}${parseFloat(
      (value_number / TRILLION).toFixed(decimals)
    ).toLocaleString()}T`;

  if (Math.abs(value_number) >= BILLION)
    return `${symbol}${parseFloat(
      (value_number / BILLION).toFixed(decimals)
    ).toLocaleString()}B`;

  if (Math.abs(value_number) >= MILLION)
    return `${symbol}${parseFloat(
      (value_number / MILLION).toFixed(decimals)
    ).toLocaleString()}M`;

  if (Math.abs(value_number) >= THOUSAND)
    return `${symbol}${parseFloat(
      (value_number / THOUSAND).toFixed(decimals)
    ).toLocaleString()}K`;

  return `${symbol}${parseFloat(
    value_number.toFixed(decimals)
  ).toLocaleString()}`;
};

/**
 * Formats price values with adaptive decimal precision based on price magnitude
 * - >= $1.00: 2 decimals (e.g., $10.12, $1.27)
 * - >= $0.10: 3 decimals (e.g., $0.987, $0.123)
 * - >= $0.01: 4 decimals (e.g., $0.0987, $0.0123)
 * - < $0.01: 5 decimals (e.g., $0.00987, $0.00123)
 */
export const formatPriceCell = (
  value: string | number | null | undefined
): string => {
  // Handle null, undefined, "NA", NaN cases
  if (value === null || value === undefined) return "N/A";
  if (value === "NA" || value === "N/A") return "N/A";
  if (value == 0 || value === "0") return "$0.00";

  // Convert string to number if needed
  const priceNumber = typeof value === "number" ? value : Number(value);

  // Check if the number is valid
  if (isNaN(priceNumber)) return "N/A";

  // Handle negative values (shouldn't happen for prices, but just in case)
  const absPrice = Math.abs(priceNumber);
  const sign = priceNumber < 0 ? "-" : "";

  // Apply adaptive decimal formatting based on price magnitude
  if (absPrice >= 1) {
    // >= $1.00: 2 decimals
    return `${sign}$${formatWithCommas(absPrice, 2)}`;
  } else if (absPrice >= 0.1) {
    // >= $0.10: 3 decimals
    return `${sign}$${formatWithCommas(absPrice, 3)}`;
  } else if (absPrice >= 0.01) {
    // >= $0.01: 4 decimals
    return `${sign}$${formatWithCommas(absPrice, 4)}`;
  } else {
    // < $0.01: 5 decimals
    return `${sign}$${formatWithCommas(absPrice, 5)}`;
  }
};

// Helper to format with commas
const formatWithCommas = (num: number, decimals: number) => {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats large numbers with comma separators without abbreviations
 * - Maintains numerical sorting order
 * - No decimals for cleaner display of large numbers
 * - Adds optional symbol prefix (e.g., $)
 */
export const formatLargeNumberCellPlain = (
  value: string | number | null | undefined,
  symbol: string = ""
): string => {
  // Handle null, undefined, "NA", NaN cases
  if (value === null || value === undefined) return "N/A";
  if (value === "NA" || value === "N/A") return "N/A";
  if (value == 0 || value === "0") return symbol ? `${symbol}0` : "0";

  // Convert string to number if needed
  const valueNumber = typeof value === "number" ? value : Number(value);

  // Check if the number is valid
  if (isNaN(valueNumber)) return "N/A";

  // Format with commas, no decimals for large numbers
  const formattedNumber = Math.round(valueNumber).toLocaleString();

  return symbol ? `${symbol}${formattedNumber}` : formattedNumber;
};

export const numericalComparator = (valueA: number | string, valueB: number | string): number => {
  const numA = Number(valueA);
  const numB = Number(valueB);
  return numA - numB;
};

// Type guard for checking if a value is a valid number
export const isValidNumber = (value: unknown): value is number => {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
};

// Utility function for formatting percentage values
export const formatPercentage = (value: number): string =>
  `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

// Utility function for formatting percentage values
export const formatRatios = (value: number, decimals: number = 2): string => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "N/A";
  return `${num.toFixed(decimals)}`;
};

// Helper function to format dates in a readable way with minute precision
export const formatDate = (dateString: string | Date): string => {
  try {
    // Handle null, undefined, or empty string
    if (!dateString) return '';
    
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return typeof dateString === 'string' ? dateString : '';
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    // Safe fallback - return empty string if all else fails
    return typeof dateString === 'string' ? dateString : '';
  }
};

// Helper function specifically for chart axis labels - formats dates without time
// Designed to be safe for ECharts formatters that might pass invalid values
export const formatChartDate = (dateString: string | Date | null | undefined): string => {
  try {
    // Handle null, undefined, or empty string
    if (!dateString) return '';

    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    // Check if date is valid
    if (isNaN(date.getTime())) {
      // If it's a string, try to return it as-is (might be already formatted)
      return typeof dateString === 'string' ? dateString : '';
    }

    // Format as date only (no time) for chart axis labels
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    // Safe fallback - return empty string or the string itself
    return typeof dateString === 'string' ? dateString : '';
  }
};

/**
 * Format large integers without decimals
 * Used for market cap, FDV, and volume to show full values with commas
 */
export function formatIntegerNoDecimals(value: number): string {
  const rounded = Math.round(value);
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}
